import { type ChangeEvent, type FormEvent } from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'

import { DOCUMENT_LIBRARY_COPY } from '#app/routes/mocra+/_form+/components/document-libraries/constants.ts'
import {
	type DocumentLibraryDraft,
	type DocumentLibraryScopeValue,
	type DocumentLibrarySystemValue,
} from '#app/routes/mocra+/_form+/components/document-libraries/types.ts'
import {
	DOCUMENT_LIBRARY_SYSTEM_OPTIONS,
	DOCUMENT_SCOPE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

type DraftErrors = Partial<Record<keyof DocumentLibraryDraft, string>>

type Props = {
	readonly formId: string
	readonly draft: DocumentLibraryDraft
	readonly errors: DraftErrors
	readonly onDraftFieldChange: <Field extends keyof DocumentLibraryDraft>(
		field: Field,
		value: DocumentLibraryDraft[Field],
	) => void
	readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function DocumentLibraryForm({
	formId,
	draft,
	errors,
	onDraftFieldChange,
	onSubmit,
}: Props) {
	const handleSystemChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange(
			'system',
			event.target.value as DocumentLibrarySystemValue,
		)
	}

	const handleScopeChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange('scope', event.target.value as DocumentLibraryScopeValue)
	}

	const handleLocationChange = (event: ChangeEvent<HTMLInputElement>) => {
		onDraftFieldChange('location', event.target.value)
	}

	return (
		<form id={formId} onSubmit={onSubmit}>
			<SelectField
				labelProps={{ children: DOCUMENT_LIBRARY_COPY.fields.systemLabel }}
				selectProps={{
					value: draft.system,
					onChange: handleSystemChange,
					onValueChange: (next) =>
						onDraftFieldChange('system', next as DocumentLibrarySystemValue),
				}}
				options={DOCUMENT_LIBRARY_SYSTEM_OPTIONS}
			/>
			<SelectField
				labelProps={{ children: DOCUMENT_LIBRARY_COPY.fields.scopeLabel }}
				selectProps={{
					value: draft.scope,
					onChange: handleScopeChange,
					onValueChange: (next) =>
						onDraftFieldChange('scope', next as DocumentLibraryScopeValue),
				}}
				options={DOCUMENT_SCOPE_OPTIONS}
			/>
			<Field
				labelProps={{ children: DOCUMENT_LIBRARY_COPY.fields.locationLabel }}
				inputProps={{
					value: draft.location,
					onChange: handleLocationChange,
					type: 'text',
				}}
				errors={errors.location ? [errors.location] : undefined}
			/>
		</form>
	)
}
